import type { Customer } from './api/types'

export async function allCustomerChoices(
  loadPage: (page: number) => Promise<{ items: Customer[]; totalPages: number }>,
) {
  const choices = new Map<string, Customer>()
  for (let page = 1; ; page++) {
    const result = await loadPage(page)
    for (const customer of result.items) choices.set(customer.id, customer)
    if (page >= result.totalPages) return [...choices.values()]
    if (!result.items.length)
      throw new Error('Customer choices changed while loading. Refresh before choosing a customer.')
  }
}

export function customerIdentityLabel(
  customer: Pick<Customer, 'name' | 'company_name' | 'customer_number'>,
) {
  return [customer.company_name || customer.name || 'Customer', customer.customer_number]
    .filter(Boolean)
    .join(' · ')
}

/** Compatibility placeholders are not contact details or a verified TIN. */
export function customerIdentityValue(
  customer: Pick<Customer, 'is_placeholder' | 'email' | 'phone' | 'tin_number'>,
  field: 'email' | 'phone' | 'tin_number',
) {
  const value = customer[field]?.trim() || ''
  if (!customer.is_placeholder) return value
  if (/^(?:PLACEHOLDER|PENDING|UNKNOWN)$/i.test(value)) return ''
  if (field === 'email' && /^placeholder\+[^@]+@xtellar\.co$/i.test(value)) return ''
  return value
}

/** An unambiguous parsed email suggests a candidate; staff must explicitly select it. */
export function customerFromExactEmail(customers: Customer[], email: string | null | undefined) {
  const normalized = email?.trim().toLowerCase()
  if (
    !normalized ||
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized) ||
    /^placeholder\+[^@]+@xtellar\.co$/i.test(normalized)
  )
    return null
  const matches = customers.filter(
    (customer) =>
      !customer.deleted_at &&
      [
        customerIdentityValue(customer, 'email'),
        ...(customer.contacts ?? []).flatMap((contact) => [
          contact.primary_email,
          ...(contact.emails ?? []),
        ]),
      ].some((candidate) => candidate?.trim().toLowerCase() === normalized),
  )
  return matches.length === 1 ? matches[0]! : null
}
